import { type FC, useEffect, useRef } from "react";
import { useFeatureParticipationRecorder, useFeatureTernary } from "@viagogo/feature";
import { FeatureSetEnum } from "src/shared/constants/FeatureSetEnum";
import { PE_KEYS } from "src/shared/constants/PageEventKeys";
import { PE_TYPE } from "src/shared/constants/PageEventTypes";
import { useInView } from "src/shared/hooks/useInView";
import { useStorePageEvent } from "src/shared/hooks/useStorePageEvent";
import { PerformerLoadingCarousels } from "src/website-apps/responsive/stubhub-performer/components/performer-carousel-skeleton/PerformerLoadingCarousels";

interface Props {
  isLoading: boolean;
}

/**
 * Loading skeleton experiment 22283/4 wrapper so that we can custom record participation after control or variant has been in view for at least 0.25s
 */
export const LoadingCarouselsExperiment: FC<Props> = ({ isLoading }) => {
  const storePageEvent = useStorePageEvent();
  const entryTimeRef = useRef<number | null>(null);
  const loggedTimeInViewRef = useRef(false);
  const { ref } = useInView({
    threshold: 0.1,
    onChange: (inView) => {
      if (inView && entryTimeRef.current === null) {
        // store the time component first comes into view
        entryTimeRef.current = Date.now();
      }
    },
  });
  const recordParticipation = useFeatureParticipationRecorder(FeatureSetEnum.PerformerCarouselsLoadingSkeleton);
  const loadingSkeletonExp = useFeatureTernary(FeatureSetEnum.PerformerCarouselsLoadingSkeleton);
  const isLoadingSkeletonVariant = loadingSkeletonExp({
    control: () => false,
    variant: () => true,
    record: false,
  });
  useEffect(() => {
    const logTimeInView = (timeSpentInView: number) => {
      // only want to do this once. on load or on "unmount and still loading"
      if (loggedTimeInViewRef.current) {
        return;
      }
      loggedTimeInViewRef.current = true;
      const floorRoundedTimeMs = Math.round(timeSpentInView / 500) * 500;
      if (floorRoundedTimeMs < 250) {
        storePageEvent(PE_TYPE.LOGGING, PE_KEYS.CAROUSEL_SKELETON_IN_VIEW_SHORT, "<0.25s");
        return;
      }
      // record only if they were in view of carousel part of page for at least 0.25s. (even if control and skeleton wont show)
      recordParticipation();
      if (!isLoadingSkeletonVariant) {
        return;
      }

      /** bucket participation using page events 0.25-0.75s, 0.75-1.25s, 1.25s-1.75s, \> 1.75s */
      if (floorRoundedTimeMs >= 250 && floorRoundedTimeMs < 750) {
        storePageEvent(PE_TYPE.LOGGING, PE_KEYS.CAROUSEL_SKELETON_IN_VIEW, "[0.25s,0.75s)");
      } else if (floorRoundedTimeMs >= 750 && floorRoundedTimeMs < 1250) {
        storePageEvent(PE_TYPE.LOGGING, PE_KEYS.CAROUSEL_SKELETON_IN_VIEW, "[0.75s-1.25s)");
      } else if (floorRoundedTimeMs >= 1250 && floorRoundedTimeMs < 1750) {
        storePageEvent(PE_TYPE.LOGGING, PE_KEYS.CAROUSEL_SKELETON_IN_VIEW, "[1.25s-1.75s)");
      } else if (floorRoundedTimeMs >= 1750) {
        storePageEvent(PE_TYPE.LOGGING, PE_KEYS.CAROUSEL_SKELETON_IN_VIEW, "[1.75s,inf)");
      }
    };
    // loading is done, calculate the total time spent in view
    if (!isLoading && entryTimeRef.current !== null) {
      const timeSpentInView = Date.now() - entryTimeRef.current;
      logTimeInView(timeSpentInView);
    }
    return () => {
      // Component is unmounting but still loading, log the total time spent in view if not already done loading
      if (!isLoading || entryTimeRef.current === null) {
        return;
      }

      const timeSpentInView = Date.now() - entryTimeRef.current;
      logTimeInView(timeSpentInView);
    };
  }, [isLoading, recordParticipation, isLoadingSkeletonVariant, storePageEvent]);

  return <div ref={ref}>{isLoading && isLoadingSkeletonVariant ? <PerformerLoadingCarousels /> : null}</div>;
};
